/**
 * Somewhere to meet again, agreed while the two can still talk.
 *
 * Measured against the live connect service on 28 July, twice, identically:
 * the moment the phone's signalling socket goes quiet the service deletes the
 * room, within two seconds, and a second later trying to rejoin that room is
 * refused. `connect/app.py` closes the room in a `finally` when either side
 * disconnects, and rooms expire after five minutes anyway. So the place where
 * the two devices met does not survive the phone being put down, and there is
 * no address left to go back to.
 *
 * Hence an appointment. While the channel is up and both sides can hear each
 * other, the desktop names the next meeting place and sends it over. Both
 * write it down. When the channel dies, the desktop goes and stands in that
 * room, and the phone, when it wakes up, walks in. Nobody scans anything.
 *
 * A fresh one is agreed on every connection, so an appointment is used once
 * and then replaced. What is written down is a room name and a one-time
 * secret: on their own they buy an introduction to a desktop and nothing else,
 * because pairing still has to happen and the token still has to be valid.
 * Anyone who could read this out of localStorage could already read the tokens
 * sitting next to it.
 */

const KEY = "novamund_rendezvous";

/** Which side of the appointment wrote this down. */
export const HOST = "host";
export const GUEST = "guest";

/** Fresh room and secret, from real randomness. Same shape as the QR's. */
export function makeAppointment(cryptoObject = globalThis.crypto) {
  const draw = (size) => {
    const bytes = new Uint8Array(size);
    cryptoObject.getRandomValues(bytes);
    return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
  };
  return { room: draw(8), secret: draw(16) };
}

/**
 * Write it down, and write down which side we are.
 *
 * The side is not decoration. Both devices store the same room under the same
 * key, and the desktop now goes and stands in that room whenever it finds one.
 * Without the side the phone would read its own copy, decide it was the
 * desktop, and open the room as host: it would then be sitting in the doorway
 * it was trying to walk through. Storing who wrote it makes that impossible
 * rather than unlikely.
 *
 * An appointment written before this existed has no side and is treated as
 * nobody's, which costs one scan and cannot deadlock.
 */
export function keepAppointment(appointment, storage = globalThis.localStorage) {
  if (!appointment?.room || !appointment?.secret) return false;
  try {
    const kept = { room: appointment.room, secret: appointment.secret };
    // Only written when there is one, so an appointment stored by an older
    // build reads back byte for byte as it did before.
    if (appointment.side) kept.side = appointment.side;
    storage.setItem(KEY, JSON.stringify(kept));
    return true;
  } catch {
    // No appointment means the old behaviour: the owner scans again. Worse,
    // and still working, which is the right way round.
    return false;
  }
}

/** What was agreed last time, or null. Carries the side, which may be null. */
export function readAppointment(storage = globalThis.localStorage) {
  try {
    const raw = storage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.room || !parsed?.secret) return null;
    const kept = { room: parsed.room, secret: parsed.secret };
    if (parsed.side) kept.side = parsed.side;
    return kept;
  } catch {
    return null;
  }
}

/** What this side agreed to be at, or null. Never somebody else's half. */
export function readAppointmentFor(side, storage = globalThis.localStorage) {
  const kept = readAppointment(storage);
  return kept && kept.side === side ? kept : null;
}

/** Forget it. For signing out, and for an appointment nobody kept. */
export function forgetAppointment(storage = globalThis.localStorage) {
  try {
    storage.removeItem(KEY);
  } catch {
    // Nothing to do about it and nothing that depends on it.
  }
}

/**
 * OPSTART-FIX, 12 OKTOBER 2026: een afspraak die bij de vorige start NIET werkte.
 *
 * Glasronde v0.9.151: twee koude starts van ongeveer 30 s voordat de QR kwam.
 * Een mislukte poging wiste niets, dus elke volgende start wachtte opnieuw. Nu
 * wordt de mislukking onthouden; de volgende start wacht dan niet, toont meteen
 * de QR en probeert op de achtergrond. Het kanaal dat terugkomt wist hem.
 */
const MISLUKT = "novamund_rendezvous_mislukt";

export function markeerMislukt(storage = globalThis.localStorage) {
  try {
    storage.setItem(MISLUKT, "1");
  } catch {
    // Zonder opslag wacht de volgende start de gewone 5 s; erger, en het werkt.
  }
}

export function wasMislukt(storage = globalThis.localStorage) {
  try {
    return storage.getItem(MISLUKT) === "1";
  } catch {
    return false;
  }
}

export function wisMislukt(storage = globalThis.localStorage) {
  try {
    storage.removeItem(MISLUKT);
  } catch {
    // niets aan te doen
  }
}
